import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOSchema from '../components/SEOSchema';
import LeadMagnet from '../components/LeadMagnet';
import {
  BookOpen, Calculator, Stethoscope, ArrowRight,
  Baby, HeartPulse, Activity, Apple, Dumbbell,
} from 'lucide-react';
import { CONTENT_HUBS, HUB_ORDER } from '../data/contentHubs';

const HUB_ICONS = { Activity, HeartPulse, Baby, Dumbbell };

const clusters = [
  {
    id: 'fertility-pmos',
    icon: HeartPulse,
    title: 'Fertility & PMOS (previously called PCOS)',
    desc: 'Doctor-guided nutrition and lifestyle support for reproductive health and hormonal balance.',
    program: { label: 'Fertility Nutrition Program', to: '/fertility-nutrition' },
    program2: { label: 'PMOS (previously called PCOS) Support', to: '/pcos-diet-plan' },
    articles: [
      { label: 'Fertility Nutrition: Foods for Hormone Balance', to: '/blog/fertility-nutrition-hormone-balance' },
      { label: 'PMOS (previously called PCOS) Diet Plan: Complete Guide', to: '/blog/pcos-diet-plan-complete-guide' },
    ],
    tools: [{ label: 'Waist-to-Hip Ratio', to: '/calculators/waist-to-hip' }, { label: 'BMI Calculator', to: '/calculators/bmi' }],
  },
  {
    id: 'pregnancy-postpartum',
    icon: Baby,
    title: 'Pregnancy & Postpartum',
    desc: 'Trimester-specific nutrition and safe postpartum recovery, supervised by our medical team.',
    program: { label: 'Pregnancy Nutrition Plan', to: '/pregnancy-nutrition' },
    program2: { label: 'Postpartum Recovery Program', to: '/postpartum-recovery' },
    articles: [
      { label: 'Best Diet Plan for Pregnancy', to: '/blog/best-diet-plan-for-pregnancy' },
      { label: 'Best Diet Plan After Delivery', to: '/blog/best-diet-plan-after-delivery' },
    ],
    tools: [{ label: 'Pregnancy Weight Gain', to: '/calculators/pregnancy-weight-gain' }],
  },
  {
    id: 'metabolic-weight',
    icon: Activity,
    title: 'Metabolic Health & Weight',
    desc: 'Evidence-based clinical nutrition for diabetes, thyroid, weight and metabolic conditions.',
    program: { label: 'Clinical Nutrition (MNT)', to: '/medical-nutrition-therapy' },
    program2: { label: 'Doctor-Led Fitness', to: '/doctor-led-fitness' },
    articles: [
      { label: 'Full Body Health Analysis', to: '/health-analysis' },
    ],
    tools: [
      { label: 'TDEE Calculator', to: '/calculators/tdee' },
      { label: 'Calorie Deficit', to: '/calculators/calorie-deficit' },
      { label: 'Body Fat %', to: '/calculators/body-fat' },
    ],
  },
  {
    id: 'nutrition-fundamentals',
    icon: Apple,
    title: 'Nutrition Fundamentals',
    desc: 'Practical, science-backed nutrition guidance for everyday eating.',
    program: { label: 'Clinical Nutrition (MNT)', to: '/medical-nutrition-therapy' },
    articles: [
      { label: 'Best Cooking Oil for Indian Cooking', to: '/blog/best-cooking-oil-indian-cooking-guide' },
      { label: 'Types of Protein Powder Compared', to: '/blog/types-of-protein-powder-comparison' },
      { label: 'Prawns vs Fish vs Chicken Protein', to: '/blog/prawns-fish-chicken-protein-comparison' },
    ],
    tools: [{ label: 'Protein Intake', to: '/calculators/protein-intake' }, { label: 'Macro Calculator', to: '/calculators/macro' }],
  },
  {
    id: 'fitness-performance',
    icon: Dumbbell,
    title: 'Fitness & Performance',
    desc: 'Safe, progressive training and conditioning with medical oversight.',
    program: { label: 'Online Fitness Coaching', to: '/online-fitness-coaching' },
    articles: [
      { label: 'Weekend Sports Nutrition & Warm-Up Guide', to: '/blog/weekend-sports-nutrition-warmup-guide' },
    ],
    tools: [
      { label: 'Heart Rate Zones', to: '/calculators/heart-rate-zones' },
      { label: 'One Rep Max', to: '/calculators/one-rep-max' },
      { label: 'VO2 Max', to: '/calculators/vo2-max' },
    ],
  },
];

const LinkRow = ({ icon: Icon, label, items }) => (
  <div className="mt-4">
    <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {label}
    </p>
    <ul className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i}>
          <Link to={it.to} className="text-blue-400 hover:text-blue-300 text-sm inline-flex items-center gap-1 group">
            {it.label}
            <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const ResourcesPage = () => (
  <div className="min-h-screen bg-slate-950">
    <Helmet>
      <title>Knowledge Hub: Guides, Calculators &amp; Programs | TCUBE FITNESS™</title>
      <meta name="description" content="The TCUBE FITNESS Knowledge Hub: doctor-written guides, free health calculators, and programs organized by topic such as fertility, pregnancy, PMOS, metabolic health, nutrition and fitness." />
      <link rel="canonical" href="https://tcubefitness.com/resources" />
    </Helmet>

    <SEOSchema
      type="webpage"
      name="TCUBE FITNESS™ Knowledge Hub"
      description="Doctor-written guides, free calculators and programs organized by health topic."
      url="/resources"
      breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Resources', path: '/resources' }]}
    />

    <Header />

    <main id="main-content" className="pt-28 sm:pt-32 pb-20" data-testid="resources-page">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <BookOpen className="w-4 h-4" aria-hidden="true" />
            Knowledge Hub
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Learn, Calculate &amp; <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Take Action</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg">
            Doctor-written guides, free health calculators and programs, organized by topic so you can find exactly what you need.
          </p>
        </div>

        {/* 4 Health Hubs */}
        <div className="max-w-5xl mx-auto mb-14">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-white mb-6">Explore our Health Hubs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HUB_ORDER.map((slug) => {
              const h = CONTENT_HUBS[slug];
              const HIcon = HUB_ICONS[h.icon] || Activity;
              return (
                <Link
                  key={slug}
                  to={`/hub/${slug}`}
                  data-testid={`resources-hub-${slug}`}
                  className="group bg-slate-900/60 border border-slate-800 hover:border-slate-600 rounded-2xl p-5 transition-all"
                >
                  <HIcon className={`w-6 h-6 ${h.accent} mb-3`} aria-hidden="true" />
                  <p className="text-white font-semibold text-sm flex items-center gap-1.5">
                    {h.title}
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                  </p>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {clusters.map((c) => (
            <div key={c.id} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-7 hover:border-slate-700 transition-colors" data-testid={`resource-cluster-${c.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 flex-shrink-0">
                  <c.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-white">{c.title}</h2>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">{c.desc}</p>

              <LinkRow icon={Stethoscope} label="Programs" items={[c.program, ...(c.program2 ? [c.program2] : [])]} />
              {c.articles?.length > 0 && <LinkRow icon={BookOpen} label="Guides & Articles" items={c.articles} />}
              {c.tools?.length > 0 && <LinkRow icon={Calculator} label="Free Tools" items={c.tools} />}
            </div>
          ))}
        </div>

        <div className="max-w-5xl mx-auto mt-12">
          <LeadMagnet />
        </div>

        <div className="max-w-5xl mx-auto mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/calculators" className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-full font-semibold transition-colors" data-testid="resources-all-calculators">
            <Calculator className="w-4 h-4" aria-hidden="true" /> Browse all 22 calculators
          </Link>
          <Link to="/blog" className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-full font-semibold transition-colors" data-testid="resources-all-blog">
            <BookOpen className="w-4 h-4" aria-hidden="true" /> Read all articles
          </Link>
        </div>
      </div>
    </main>

    <Footer />
  </div>
);

export default ResourcesPage;
